import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useHasBookkeepingAccountData } from '@features/accounts/hooks/useHasBookkeepingAccountData'
import { BankAccountSource } from '@features/bankAccounts/enums/bankAccountSource'
import { useBankAccountByBookkeepingAccountId } from '@features/bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { useShouldConnectBank } from '@modals'
import { SortProperty } from '@modules-deprecated/bankReconciliation/enums/sortProperty'
import { useAccountBankLines } from '@modules-deprecated/bankReconciliation/hooks/useAccountBankLines'
import { BankLine, Difference } from '@modules-deprecated/bankReconciliation/types'

import { SortDirection } from '../../../enums/sortDirection'
import { BankLinesState } from '../../paymentReconciliationModal/enums/bankLinesState'
import { filterAndSortBankLines } from '../../paymentReconciliationModal/utils/filterAndSortBankLines'
import { BankLinesSortingProperty } from '../elements/BankLinesModalBody/elements/BankLinesActions/types/bankLinesSortingProperty'
import { useConnectBankPrompt } from '../hooks/useConnectBankPrompt'
import { getSubjectReconciliationDifference, getTypeFromPayableSubject } from '../utils'
import { getCashSide } from '../utils/getCashSide'
import { useBankLinesFilters } from './bankLinesFilteringContext'
import { useMultiplePayableSubjects } from './multiplePayableSubjectsContext'
import { usePaymentModal } from './paymentModalContext'

interface ContextState {
  bankLines?: BankLine[]
  bankLinesDifference: Difference
  bankLinesSummedAmounts: number
  bankLinesState: BankLinesState
  hasBankLinesDifference: boolean
  isLoading: boolean
  newestBankLineDate: string
  selectedBankLinesIds: string[]
  setBankLinesState: Dispatch<SetStateAction<BankLinesState>>
  setIsPromptHidden: (arg: boolean) => void
  setSelectedBankLinesIds: Dispatch<SetStateAction<string[]>>
}

const BankLinesContext = createContext<ContextState | undefined>(undefined)

interface BankLinesContextProps {
  children?: ReactNode
}

export const BankLinesContextProvider = ({ children }: BankLinesContextProps): ReactElement => {
  const [selectedBankLinesIds, setSelectedBankLinesIds] = useState<string[]>([])

  const { exchangeRate, selectedAccount, selectedAccountId, reconciliationStartDate } = usePaymentModal()
  const { selectedSubjectsAmountsSum, convertedSubjectsAmountsSum, initialSubject, isCreditNoteSelected } =
    useMultiplePayableSubjects()
  const { bankLines, isLoading: isLoadingBankLines } = useAccountBankLines(
    {
      accountId: selectedAccountId,
      isReconciled: false,
      sortDirection: SortDirection.Desc,
      sortProperty: SortProperty.EntryDate,
      entryDatePeriod: reconciliationStartDate ? `from:${reconciliationStartDate}` : undefined,
    },
    !!selectedAccountId,
  )

  const { isPromptHidden, setIsPromptHidden } = useConnectBankPrompt(selectedAccount)

  const { bankLinesSearchQuery, bankLinesSortingProperty } = useBankLinesFilters()
  const [bankLinesState, setBankLinesState] = useState(BankLinesState.Empty)
  const {
    isLoading: isLoadingBankConnections,
    shouldConnectBank,
    shouldRenewBankConnection,
  } = useShouldConnectBank(selectedAccountId)
  const { source: bankAccountSource, isLoading: isLoadingBankAccount } =
    useBankAccountByBookkeepingAccountId(selectedAccountId)

  const isBillySourcedAccount = bankAccountSource === BankAccountSource.BILLY
  const shouldCheckForExistingBankAccountData = isBillySourcedAccount && !isLoadingBankLines && bankLines?.length === 0

  const { bookkeepingAccountHasExistingData, isLoading: isLoadingBookkeepingAccountData } =
    useHasBookkeepingAccountData({
      selectedBookkeepingAccount: shouldCheckForExistingBankAccountData ? selectedAccount : undefined,
    })

  const isManualBankAccount = isBillySourcedAccount && !!bookkeepingAccountHasExistingData
  const subjectType = getTypeFromPayableSubject(initialSubject)

  const bankLinesFilteredAndSorted = useMemo(() => {
    return filterAndSortBankLines({
      bankLines,
      subjectsAmount: convertedSubjectsAmountsSum,
      searchQuery: bankLinesSearchQuery,
      side: getCashSide(subjectType, initialSubject?.type),
      sortingProperty: bankLinesSortingProperty,
    })
  }, [
    bankLines,
    bankLinesSearchQuery,
    bankLinesSortingProperty,
    convertedSubjectsAmountsSum,
    subjectType,
    initialSubject?.type,
  ])

  const selectedBankLines = useMemo(() => {
    return bankLines?.filter((bankLine: BankLine) => selectedBankLinesIds.includes(bankLine.id)) || []
  }, [bankLines, selectedBankLinesIds])

  const bankLinesSummedAmounts = useMemo(
    () => selectedBankLines.reduce((sum, { amount }) => sum + amount, 0),
    [selectedBankLines],
  )

  const bankLinesDifference = useMemo(() => {
    return getSubjectReconciliationDifference({
      bankLines: selectedBankLines,
      isCreditNote: isCreditNoteSelected,
      reconciliationAmount: bankLinesSummedAmounts,
      subjectsAmount: selectedSubjectsAmountsSum,
      subjectType,
    })
  }, [selectedBankLines, isCreditNoteSelected, bankLinesSummedAmounts, selectedSubjectsAmountsSum, subjectType])

  const hasBankLinesDifference = useMemo(() => {
    return !!bankLinesDifference?.differenceAmount
  }, [bankLinesDifference?.differenceAmount])

  const isSelectedBankAccount = !!selectedAccount?.isBankAccount
  const isLoading =
    isLoadingBankLines || isLoadingBankConnections || isLoadingBankAccount || isLoadingBookkeepingAccountData

  const newestBankLineDate = useMemo(() => {
    const bankLinesSortedNewestToOldest = filterAndSortBankLines({
      bankLines: selectedBankLines,
      sortingProperty: BankLinesSortingProperty.DateDesc,
    })

    return bankLinesSortedNewestToOldest[0]?.entryDate
  }, [selectedBankLines])

  useEffect(() => {
    let bankLinesState = BankLinesState.Empty

    if (bankLinesFilteredAndSorted?.length) {
      bankLinesState = BankLinesState.BankLinesList
    }

    if (!isLoading && shouldConnectBank && !isManualBankAccount && bankLines?.length === 0 && !isPromptHidden) {
      bankLinesState = BankLinesState.ConnectBank
    }

    if (!isLoading && shouldRenewBankConnection) {
      bankLinesState = BankLinesState.RenewBankConnection
    }

    setBankLinesState(bankLinesState)
  }, [
    bankLinesFilteredAndSorted?.length,
    bankLines?.length,
    exchangeRate,
    selectedAccountId,
    isSelectedBankAccount,
    isManualBankAccount,
    shouldConnectBank,
    shouldRenewBankConnection,
    isLoading,
    isPromptHidden,
  ])

  const contextValue = useMemo(
    () => ({
      bankLines: bankLinesFilteredAndSorted,
      bankLinesDifference,
      bankLinesSummedAmounts,
      bankLinesState,
      hasBankLinesDifference,
      isLoading,
      newestBankLineDate,
      selectedBankLinesIds,
      setBankLinesState,
      setIsPromptHidden,
      setSelectedBankLinesIds,
    }),
    [
      bankLinesFilteredAndSorted,
      bankLinesDifference,
      bankLinesSummedAmounts,
      bankLinesState,
      hasBankLinesDifference,
      isLoading,
      newestBankLineDate,
      selectedBankLinesIds,
      setIsPromptHidden,
    ],
  )

  return <BankLinesContext.Provider value={contextValue}>{children}</BankLinesContext.Provider>
}

export const useBankLines = () => {
  const context = useContext(BankLinesContext)

  if (!context) {
    throw new Error('BankLinesContextProvider is missing in the module!')
  }

  return context
}
